import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErroCarregamento, { extrairMensagemErro } from '../../components/common/ErroCarregamento';
import MultiSelect from '../../components/common/MultiSelect';
import { useAuth } from '../../contexts/AuthContext';
import { usePresentation } from '../../contexts/PresentationContext';
import logoPrimary from '../../assets/images/logo-primary.png';
import logoHorizontal from '../../assets/images/logo-horizontal.png';
import * as hospitaisApi from '../../api/hospitais';
import './ModoExibicao.css';

function primeiroNome(nome = '') {
  return nome.trim().split(/\s+/)[0];
}

export default function ModoExibicao() {
  const { user } = useAuth();
  const { apresentacaoAtiva, hospitaisPermitidos, escolherAdministracao, escolherApresentacao } = usePresentation();
  const navigate = useNavigate();

  const [hospitais, setHospitais] = useState(null);
  const [erro, setErro] = useState('');
  const [mostrarSelecao, setMostrarSelecao] = useState(false);
  const [idsSelecionados, setIdsSelecionados] = useState(() => (apresentacaoAtiva ? hospitaisPermitidos : []));

  const carregar = async () => {
    setErro('');
    try {
      const { data } = await hospitaisApi.listar();
      setHospitais(data);
    } catch (err) {
      setErro(extrairMensagemErro(err, 'Não foi possível carregar os hospitais.'));
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  // auditor_hospital é escopado a um único hospital, igual cliente — não escolhe modo de exibição
  if (user?.perfil === 'cliente' || user?.perfil === 'auditor_hospital') {
    return <Navigate to={`/hospitais/${user.id_hospital}`} replace />;
  }

  // Se essa tela for reaberta (botão voltar do navegador, URL digitada) enquanto uma
  // apresentação já está em andamento, o seletor só pode mostrar/reter os hospitais já
  // permitidos — nunca a lista inteira. Ampliar a seleção exige sair da apresentação primeiro
  // (Modo Administração), uma ação deliberada, não um acidente de navegação.
  const hospitaisSelecionaveis =
    apresentacaoAtiva && hospitais ? hospitais.filter((h) => hospitaisPermitidos.includes(h.id_hospital)) : hospitais;

  const entrarAdministracao = () => {
    escolherAdministracao();
    navigate('/dashboard', { replace: true });
  };

  const confirmarApresentacao = () => {
    if (idsSelecionados.length === 0) return;
    escolherApresentacao(idsSelecionados);
    navigate('/dashboard', { replace: true });
  };

  return (
    <div className="modo-exibicao-page">
      {/* ===== Painel esquerdo — marca ===== */}
      <div className="modo-exibicao-brand">
        <div className="modo-exibicao-brand__bars" aria-hidden="true">
          <i className="modo-exibicao-brand__bar modo-exibicao-brand__bar--1" />
          <i className="modo-exibicao-brand__bar modo-exibicao-brand__bar--2" />
          <i className="modo-exibicao-brand__bar modo-exibicao-brand__bar--3" />
        </div>

        <div className="modo-exibicao-brand__hero">
          <img src={logoPrimary} alt="AM Gestão" className="modo-exibicao-brand__logo" />
          <div className="modo-exibicao-brand__rule" />
          <p className="modo-exibicao-brand__text">
            Plataforma de gestão da jornada de acreditação hospitalar com base nos padrões da
            ONA.
          </p>
        </div>

        <div className="modo-exibicao-brand__footer">
          Desenvolvido por <strong>Datafit</strong>
        </div>
      </div>

      {/* ===== Painel direito — escolha de modo ===== */}
      <div className="modo-exibicao-panel">
        <div className="modo-exibicao-panel__inner">
          <div className="modo-exibicao-panel__logo">
            <img src={logoHorizontal} alt="AM Gestão" />
          </div>

          <h1 className="modo-exibicao-panel__title">
            {user?.nome ? `Olá, ${primeiroNome(user.nome)}` : 'Como você quer entrar?'}
          </h1>

          {!hospitais ? (
            erro ? (
              <ErroCarregamento mensagem={erro} onRetry={carregar} />
            ) : (
              <LoadingSpinner />
            )
          ) : !mostrarSelecao ? (
            <div className="modo-exibicao__opcoes">
              <button type="button" className="modo-exibicao__opcao" onClick={entrarAdministracao}>
                <span className="ms modo-exibicao__opcao-icone">admin_panel_settings</span>
                <div>
                  <div className="modo-exibicao__opcao-titulo">Modo Administração</div>
                  <div className="modo-exibicao__opcao-desc">
                    Acesso completo a todos os hospitais, usuários e auditoria — uso normal do dia a dia.
                  </div>
                </div>
              </button>

              <button type="button" className="modo-exibicao__opcao" onClick={() => setMostrarSelecao(true)}>
                <span className="ms modo-exibicao__opcao-icone">present_to_all</span>
                <div>
                  <div className="modo-exibicao__opcao-titulo">Modo Apresentação</div>
                  <div className="modo-exibicao__opcao-desc">
                    Escolha um ou mais hospitais — o sistema só mostra dados deles, nada dos demais aparece.
                  </div>
                </div>
              </button>
            </div>
          ) : (
            <div className="modo-exibicao__selecao">
              <label className="am-field__label mb-1" style={{ display: 'block' }}>
                Quais hospitais você vai apresentar?
              </label>
              <MultiSelect
                options={hospitaisSelecionaveis.map((h) => ({ value: h.id_hospital, label: h.nome }))}
                selected={idsSelecionados}
                onChange={setIdsSelecionados}
                placeholder="Buscar hospital..."
              />

              <div className="modo-exibicao__acoes">
                <button type="button" className="am-btn am-btn-outline" onClick={() => setMostrarSelecao(false)}>
                  Voltar
                </button>
                <button
                  type="button"
                  className="am-btn am-btn-dark"
                  disabled={idsSelecionados.length === 0}
                  onClick={confirmarApresentacao}
                >
                  <span className="ms">play_arrow</span>
                  Entrar em modo apresentação
                </button>
              </div>
            </div>
          )}

          <div className="modo-exibicao-panel__footer">ACF Moura · Consultoria ONA © 2026</div>
        </div>
      </div>
    </div>
  );
}
